import { toast } from 'sonner';
import { isApiError, safeMessage } from '@/api/errors';

export interface NotifyOptions {
  description?: string;
  /** For links such as "View in audit log": navigate in `onClick`. */
  action?: { label: string; onClick: () => void };
}

const FALLBACK = 'Something went wrong.';

const toastOptions = ({ description, action }: NotifyOptions) => ({
  ...(description ? { description } : {}),
  ...(action ? { action } : {}),
});

/** User-safe sentences for any thrown value. ApiError messages are already safe; others pass through `safeMessage`. */
export function errorMessages(error: unknown, fallback = FALLBACK): string[] {
  if (isApiError(error)) return error.messages.length > 0 ? error.messages : [fallback];
  const text = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  return text.trim() ? [safeMessage(text.trim())] : [fallback];
}

/** What "Copy details" puts on the clipboard: status, code and messages. Never headers, tokens or raw bodies. */
export function errorDetails(error: unknown): string {
  const details = isApiError(error)
    ? { status: error.status, code: error.code, messages: error.messages }
    : { status: null, code: 'client', messages: errorMessages(error) };
  return JSON.stringify(details, null, 2);
}

export function notifySuccess(message: string, options: NotifyOptions = {}) {
  return toast.success(message, toastOptions(options));
}

export function notifyInfo(message: string, options: NotifyOptions = {}) {
  return toast.info(message, toastOptions(options));
}

export function notifyError(error: unknown, fallback = FALLBACK) {
  const [title = fallback, ...rest] = errorMessages(error, fallback);
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(errorDetails(error));
      notifyInfo('Error details copied');
    } catch {
      // No clipboard access (insecure context or denied): nothing else to offer.
    }
  };
  return toast.error(title, {
    ...(rest.length > 0 ? { description: rest.join(' ') } : {}),
    action: { label: 'Copy details', onClick: () => void copy() },
  });
}
